/**
 * TopbarSubmenuCanopy
 *
 * For menus WITH `categories` (e.g. Masters):
 *   - Resting: category names rendered as tab-links, identical styling to the
 *     existing flat .topbar-submenu-link tabs. The category that owns the
 *     currently active submenu gets the same primary ::after underline.
 *   - Hover: smooth animated canopy drops down — a multi-column grid of all
 *     categories and their submenus (text-only, no icons).
 *
 * For simple flat menus (all other modules):
 *   - Renders the existing flat topbar-submenu-link row unchanged.
 *
 * Hover intent: 160 ms debounce on mouse-leave to prevent accidental flicker.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { getMenuCategories, hasChildNavItems } from '../../config/navigation'
import { useAuth } from '../../hooks/useAuth'
import NavChevronIndicator from '../../components/NavChevronIndicator'
import './TopbarSubmenuCanopy.css'

// ─── Canopy column (one per category) ────────────────────────────────────────

function CategoryColumn({ category, activeSubmenuId, onNavigate, hasModule, getRequiredModule, hasPrivilege, isAdmin }) {
  const visibleSubmenus = category.submenus.filter((item) => {
    if (!isAdmin && item.privilege && hasPrivilege && !hasPrivilege(item.privilege)) return false
    const req = item.requiredModule || (getRequiredModule ? getRequiredModule(item.path) : null)
    if (req && hasModule && !hasModule(req)) return false
    return true
  })

  if (visibleSubmenus.length === 0) return null

  return (
    <div className="canopy-column">
      <div className="canopy-column-header">
        <span className="canopy-column-title">{category.label}</span>
      </div>
      <ul className="canopy-column-list" role="list">
        {visibleSubmenus.map((item) => (
          <li key={item.id}>
            {item.comingSoon ? (
              <span className="canopy-item canopy-item--soon" title="Coming soon">
                <span className="canopy-item-label">{item.label}</span>
                <span className="canopy-item-soon-badge">Soon</span>
              </span>
            ) : (
              <button
                type="button"
                className={`canopy-item${activeSubmenuId === item.id ? ' canopy-item--active' : ''}`}
                onClick={() => onNavigate(item.path)}
              >
                <span className="canopy-item-label">{item.label}</span>
                {activeSubmenuId === item.id && (
                  <span className="canopy-item-active-dot" aria-hidden="true" />
                )}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

// ─── Categorized canopy (Masters + future categorized menus) ─────────────────

function CategorizedCanopy({ menu, activeSubmenuId, hasModule, getRequiredModule, hasPrivilege, isAdmin }) {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)
  const [hoveredCatId, setHoveredCatId] = useState(null)
  const collapseTimerRef = useRef(null)
  const rootRef = useRef(null)

  const categories = getMenuCategories(menu)

  // Which category currently owns the active submenu?
  const activeCategoryId = (
    categories.find((cat) => cat.submenus.some((s) => s.id === activeSubmenuId))
    || categories[0]
  )?.id

  const expand = useCallback(() => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current)
      collapseTimerRef.current = null
    }
    setExpanded(true)
  }, [])

  const scheduleCollapse = useCallback(() => {
    collapseTimerRef.current = setTimeout(() => setExpanded(false), 160)
  }, [])

  // Escape collapses
  useEffect(() => {
    if (!expanded) return
    const handler = (e) => {
      if (e.key === 'Escape') {
        setExpanded(false)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [expanded])

  // Outside-click collapses
  useEffect(() => {
    if (!expanded) return
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setExpanded(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [expanded])

  const handleNavigate = useCallback(
    (path) => {
      setExpanded(false)
      navigate(path)
    },
    [navigate],
  )

  // Filter categories that have at least one visible submenu
  const visibleCategories = categories.filter((cat) => {
    return cat.submenus.some((s) => {
      if (!isAdmin && s.privilege && hasPrivilege && !hasPrivilege(s.privilege)) return false
      if (!getRequiredModule || !hasModule) return true
      const req = getRequiredModule(s.path)
      return !req || hasModule(req)
    })
  })

  return (
    <div
      className="canopy-root"
      ref={rootRef}
      onMouseEnter={expand}
      onMouseLeave={scheduleCollapse}
    >
      {/* ── Resting state: category name tabs, styled like topbar-submenu-link ── */}
      <nav
        className={`canopy-strip topbar-submenu${expanded ? ' canopy-strip--open' : ''}`}
        aria-label={`${menu.label} categories`}
      >
        {visibleCategories.map((cat) => {
          const hasChildren = hasChildNavItems(cat)
          const isCatHovered = hoveredCatId === cat.id
          return (
            <button
              key={cat.id}
              type="button"
              className={`topbar-submenu-link canopy-strip-tab${cat.id === activeCategoryId ? ' active' : ''}`}
              onMouseEnter={() => setHoveredCatId(cat.id)}
              onMouseLeave={() => setHoveredCatId(null)}
              onClick={() => {
                // Click on any category tab → navigate to first accessible submenu, preferring submenus within this menu's basePath
                const accessibleSubmenus = cat.submenus.filter((s) => {
                  if (s.comingSoon) return false
                  if (!isAdmin && s.privilege && hasPrivilege && !hasPrivilege(s.privilege)) return false
                  if (getRequiredModule && hasModule) {
                    const req = getRequiredModule(s.path)
                    if (req && !hasModule(req)) return false
                  }
                  return true
                })
                const first = accessibleSubmenus[0]
                if (first) handleNavigate(first.path)
              }}
              aria-expanded={expanded}
            >
              <span>{cat.label}</span>
              {hasChildren && (
                <NavChevronIndicator
                  isHovered={isCatHovered}
                />
              )}
            </button>
          )
        })}
      </nav>

      {/* ── Expanded canopy grid ── */}
      <div
        className={`canopy-panel${expanded ? ' canopy-panel--open' : ''}`}
        role="navigation"
        aria-label={`${menu.label} full menu`}
      >
        <div className="canopy-grid">
          {visibleCategories.map((cat) => (
            <CategoryColumn
              key={cat.id}
              category={cat}
              activeSubmenuId={activeSubmenuId}
              onNavigate={handleNavigate}
              hasModule={hasModule}
              getRequiredModule={getRequiredModule}
              hasPrivilege={hasPrivilege}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Flat submenu row (all non-categorized menus) ────────────────────────────

function FlatSubmenuRow({
  submenus,
  activeSubmenuId,
  hasModule,
  getRequiredModule,
  reportTopTabs,
  isReportMenu,
  hasPrivilege,
  isAdmin,
}) {
  const [hoveredSubmenuId, setHoveredSubmenuId] = useState(null)
  const tabs = isReportMenu ? reportTopTabs : null

  if (tabs) {
    return (
      <nav className="topbar-submenu" aria-label="Submodule Navigation">
        {tabs.map((tab) => {
          const hasChildren = hasChildNavItems(tab)
          const isHovered = hoveredSubmenuId === tab.id
          return tab.enabled ? (
            <NavLink
              key={tab.id}
              to={tab.to}
              className={`topbar-submenu-link ${tab.active ? 'active' : ''}`}
              onMouseEnter={() => setHoveredSubmenuId(tab.id)}
              onMouseLeave={() => setHoveredSubmenuId(null)}
            >
              <span>{tab.label}</span>
              {hasChildren && (
                <NavChevronIndicator
                  isHovered={isHovered}
                />
              )}
            </NavLink>
          ) : (
            <span
              key={tab.id}
              className="topbar-submenu-link disabled"
              aria-disabled="true"
              onMouseEnter={() => setHoveredSubmenuId(tab.id)}
              onMouseLeave={() => setHoveredSubmenuId(null)}
            >
              <span>{tab.label}</span>
              {hasChildren && (
                <NavChevronIndicator
                  isHovered={isHovered}
                />
              )}
            </span>
          )
        })}
      </nav>
    )
  }

  const visible = submenus.filter((s) => {
    if (!isAdmin && s.privilege && hasPrivilege && !hasPrivilege(s.privilege)) return false
    if (!getRequiredModule || !hasModule) return true
    const req = getRequiredModule(s.path)
    return !req || hasModule(req)
  })

  return (
    <nav className="topbar-submenu" aria-label="Submodule Navigation">
      {visible.map((submenu) => {
        const hasChildren = hasChildNavItems(submenu)
        const isHovered = hoveredSubmenuId === submenu.id
        return (
          <NavLink
            key={submenu.id}
            to={submenu.path}
            className={`topbar-submenu-link ${submenu.id === activeSubmenuId ? 'active' : ''}`}
            onMouseEnter={() => setHoveredSubmenuId(submenu.id)}
            onMouseLeave={() => setHoveredSubmenuId(null)}
          >
            <span>{submenu.label}</span>
            {hasChildren && (
              <NavChevronIndicator
                isHovered={isHovered}
              />
            )}
          </NavLink>
        )
      })}
    </nav>
  )
}

// ─── Public export ────────────────────────────────────────────────────────────

/**
 * Props:
 *  menu              — active top-level menu object from MENU_STRUCTURE
 *  activeSubmenuId   — id of the currently active submenu
 *  isNotificationsRoute — true on /notifications (renders plain label)
 *  isReportMenu      — true when on /reports/* to use reportTopTabs
 *  reportTopTabs     — tab config for report schedules vs all reports
 *  hasModule         — (moduleName: string) => boolean
 *  getRequiredModule — (path: string) => string | null
 */
export default function TopbarSubmenuCanopy({
  menu,
  activeSubmenuId,
  isNotificationsRoute,
  isReportMenu,
  reportTopTabs,
  isEmailSettingsRoute,
  emailSettingsTopTabs = [],
  hasModule,
  getRequiredModule,
}) {
  const { hasPrivilege, isAdmin } = useAuth()

  if (isEmailSettingsRoute && Array.isArray(emailSettingsTopTabs) && emailSettingsTopTabs.length > 0) {
    return (
      <nav className="topbar-submenu" aria-label="Email Settings Navigation">
        {emailSettingsTopTabs.map((tab) => (
          <NavLink
            key={tab.id}
            to={tab.to}
            className={`topbar-submenu-link ${tab.active ? 'active' : ''}`}
          >
            <span>{tab.label}</span>
          </NavLink>
        ))}
      </nav>
    )
  }

  if (isNotificationsRoute) {
    return (
      <nav className="topbar-submenu" aria-label="Submodule Navigation">
        <span className="topbar-submenu-link active">
          <span>Notifications</span>
        </span>
      </nav>
    )
  }

  // Categorized menus → canopy with category tabs
  if (Array.isArray(menu?.categories)) {
    return (
      <CategorizedCanopy
        menu={menu}
        activeSubmenuId={activeSubmenuId}
        hasModule={hasModule}
        getRequiredModule={getRequiredModule}
        hasPrivilege={hasPrivilege}
        isAdmin={isAdmin}
      />
    )
  }

  // All other menus → unchanged flat row
  return (
    <FlatSubmenuRow
      submenus={menu?.submenus || []}
      activeSubmenuId={activeSubmenuId}
      hasModule={hasModule}
      getRequiredModule={getRequiredModule}
      isReportMenu={isReportMenu}
      reportTopTabs={reportTopTabs}
    />
  )
}
